import { useEffect, useCallback } from 'react'
import { UseFormReturn, FieldValues, Path, PathValue } from 'react-hook-form'

type DraftStorageOptions<T extends FieldValues> = {
  storageKey: string
  form: UseFormReturn<T>
  fieldsToSave: (keyof T)[]
  saveInterval?: number
}

const useDraftStorage = <T extends FieldValues>({
  storageKey,
  form,
  fieldsToSave,
  saveInterval = 10000,
}: DraftStorageOptions<T>) => {
  const clearSavedDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey)
    } catch {
      // localStorage may not be available
    }
  }, [storageKey])

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const parsed = JSON.parse(saved)
        fieldsToSave.forEach((field) => {
          if (parsed[field] !== undefined) {
            form.setValue(
              field as Path<T>,
              parsed[field] as PathValue<T, Path<T>>,
            )
          }
        })
      }
    } catch {
      // localStorage may not be available or data may be corrupted
    }
  }, [storageKey])

  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const values = form.getValues()
        const dataToSave = fieldsToSave.reduce((acc, field) => {
          acc[field as string] = values[field]
          return acc
        }, {} as Record<string, unknown>)
        localStorage.setItem(storageKey, JSON.stringify(dataToSave))
      } catch {
        // localStorage may not be available
      }
    }, saveInterval)

    return () => clearInterval(interval)
  }, [storageKey, form, fieldsToSave, saveInterval])

  return { clearSavedDraft }
}

export default useDraftStorage
